<template>
  <header
    :class="['ui-topbar', { sticky: isSticky }]"
    id="header"
  >
    <!-- Left: menu (tablet/mobile) + brand -->
    <div class="ui-topbar-left">
      <button
        class="ui-icon-btn ui-topbar-burger"
        id="header-burger-menu"
        type="button"
        :aria-label="$t('header.openMenu')"
        @click="stateStoreInstance.onChange"
      >
        <i class="ph ph-list" aria-hidden="true"></i>
      </button>
      <router-link to="/" class="ui-topbar-brand" :aria-label="$t('header.home')">
        <img :src="logoSrc" alt="" />
      </router-link>
    </div>

    <!-- Right: language, theme, notifications, profile -->
    <div class="ui-topbar-right">
      <LanguageSwitcher />
      <LightDarkSwtichBtn />

      <div class="dropdown">
        <button
          class="ui-icon-btn ui-topbar-bell"
          type="button"
          data-bs-toggle="dropdown"
          data-bs-display="static"
          aria-expanded="false"
          :aria-label="$t('header.notifications')"
        >
          <i class="ph ph-bell" aria-hidden="true"></i>
          <span v-if="unreadCount" class="ui-topbar-count">{{ unreadCount > 99 ? "99+" : unreadCount }}</span>
        </button>
        <div class="dropdown-menu dropdown-menu-end ui-topbar-menu ui-alerts-menu">
          <div class="ui-topbar-menu-head">
            <span>{{ $t("header.notifications") }}</span>
            <span class="ui-topbar-menu-muted">({{ unreadCount }})</span>
            <button
              v-if="unreadCount"
              type="button"
              class="ui-alerts-readall"
              @click.stop="markAllRead"
            >
              {{ $t("header.markAllRead") }}
            </button>
          </div>
          <p v-if="!alerts.length" class="ui-topbar-menu-empty">{{ $t("header.noNotifications") }}</p>
          <div v-else class="ui-alerts-list">
            <button
              v-for="alert in alerts"
              :key="alert.id"
              type="button"
              :class="['ui-alert-item', { 'ui-alert-item--unread': !alert.read_at }]"
              @click="markRead(alert)"
            >
              <span class="ui-alert-title">{{ alert.title }}</span>
              <span class="ui-alert-body">{{ alert.body }}</span>
              <span class="ui-alert-date">{{ formatAlertDate(alert.created_at) }}</span>
            </button>
          </div>
        </div>
      </div>

      <div class="dropdown">
        <button
          class="ui-topbar-profile"
          type="button"
          data-bs-toggle="dropdown"
          data-bs-display="static"
          aria-expanded="false"
        >
          <span class="ui-topbar-avatar">
            <img
              v-if="user?.pic"
              :src="user.pic"
              width="36"
              height="36"
              alt="admin"
            />
            <img
              v-else
              :src="require('@/assets/images/NoImage.png')"
              width="36"
              height="36"
              alt="admin"
            />
            <span class="ui-topbar-online" aria-hidden="true"></span>
          </span>
          <span class="ui-topbar-profile-text">
            <span class="ui-topbar-name">{{ user?.name }}</span>
            <span class="ui-topbar-role">{{ roleLabel }}</span>
          </span>
          <i class="ph ph-caret-down ui-topbar-chevron" aria-hidden="true"></i>
        </button>
        <div class="dropdown-menu dropdown-menu-end ui-topbar-menu">
          <div class="ui-topbar-menu-head ui-topbar-menu-user">
            <span class="text-truncate">{{ user?.name }}</span>
            <span class="ui-topbar-menu-online">{{ $t("header.onlineAccount") }}</span>
          </div>
          <router-link to="/profile" class="dropdown-item">
            <i class="ph ph-user-circle" aria-hidden="true"></i>
            {{ $t("header.myProfile") }}
          </router-link>
          <button
            type="button"
            class="dropdown-item text-danger"
            @click="handleLogout()"
          >
            <i class="ph ph-sign-out" aria-hidden="true"></i>
            {{ $t("header.logout") }}
          </button>
        </div>
      </div>
    </div>
  </header>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, onUnmounted } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import LightDarkSwtichBtn from "./LightDarkSwtichBtn.vue";
import LanguageSwitcher from "./LanguageSwitcher.vue";
import stateStore from "../../utils/store";
import { useAuthStore } from "@/stores/auth";
import { useSettingsStore } from "@/stores/settings";
import api from "@/utils/axios";
import { dateLocale } from "@/i18n";

interface UserAlert {
  id: number;
  title: string;
  body: string | null;
  parcel_code: string | null;
  link: string | null;
  read_at: string | null;
  created_at: string;
}

const ALERTS_URL = "/api/auth/user-alerts";
const ALERTS_POLL_MS = 60000;

export default defineComponent({
  name: "MainHeader",
  components: {
    LightDarkSwtichBtn,
    LanguageSwitcher,
  },
  setup() {
    const { t } = useI18n();
    const stateStoreInstance = stateStore;
    const isSticky = ref(false);
    const router = useRouter();
    const auth = useAuthStore();
    const user = auth.user;
    // Settings are fetched by MainSidebar when the layout is shown.
    const settingsStore = useSettingsStore();
    const logoSrc = computed(() => settingsStore.logo || require("../../assets/images/logo.png"));
    const roleLabel = computed(() => {
      const role = Number((auth.user as any)?.role_id);
      return role === 4 ? t("header.roleDriver") : role === 3 ? t("header.roleZoneManager") : "";
    });

    // In-app alerts (e.g. auto-returned parcels not handed back to the agency)
    const alerts = ref<UserAlert[]>([]);
    const unreadCount = ref(0);
    let alertsTimer: ReturnType<typeof setInterval> | undefined;

    async function fetchAlerts() {
      // no authenticated user (e.g. logged out / token cleared): skip the call
      if (!auth.token || !auth.user) {
        alerts.value = [];
        unreadCount.value = 0;
        return;
      }
      try {
        const res = await api.get(ALERTS_URL);
        alerts.value = res.data.alerts ?? [];
        unreadCount.value = res.data.unread ?? 0;
      } catch {
        // the bell stays as it is; next poll retries
      }
    }

    async function markRead(alert: UserAlert) {
      if (alert.link) router.push(alert.link);
      if (alert.read_at) return;
      alert.read_at = new Date().toISOString();
      unreadCount.value = Math.max(0, unreadCount.value - 1);
      try {
        await api.post(`${ALERTS_URL}/${alert.id}/read`);
      } catch {
        fetchAlerts();
      }
    }

    async function markAllRead() {
      try {
        await api.post(`${ALERTS_URL}/read-all`);
      } finally {
        fetchAlerts();
      }
    }

    function formatAlertDate(value: string) {
      return new Date(value).toLocaleString(dateLocale(), {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    }

    onMounted(() => {
      window.addEventListener("scroll", () => {
        let scrollPos = window.scrollY;
        isSticky.value = scrollPos >= 100;
      });
      fetchAlerts();
      alertsTimer = setInterval(fetchAlerts, ALERTS_POLL_MS);
    });

    onUnmounted(() => {
      clearInterval(alertsTimer);
    });

    function handleLogout() {
      clearInterval(alertsTimer);
      // Example: clear auth state
      auth.clearAuth();

      // Optional: redirect to login
      window.location.href = "/login";
    }
    return {
      isSticky,
      stateStoreInstance,
      user,
      logoSrc,
      roleLabel,
      handleLogout,
      alerts,
      unreadCount,
      markRead,
      markAllRead,
      formatAlertDate,
    };
  },
});
</script>

<style scoped>
.ui-topbar {
  position: fixed;
  z-index: 1020;
  top: var(--ui-frame-gap);
  left: var(--ui-content-left);
  right: var(--ui-frame-gap);
  height: var(--ui-topbar-height);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--ui-space-4);
  padding: 0 12px 0 16px;
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius-pill);
  background: var(--ui-surface);
  box-shadow: var(--ui-shadow-bar);
  transition: box-shadow var(--ui-dur) var(--ui-ease), background-color var(--ui-dur) var(--ui-ease),
    left 280ms var(--ui-ease-spring), right 280ms var(--ui-ease-spring);
}
.ui-topbar.sticky {
  box-shadow: 0 14px 40px rgba(var(--ui-accent-rgb), 0.14);
}
:global(.dark) .ui-topbar.sticky {
  box-shadow: var(--ui-shadow-bar);
}

.ui-topbar-left,
.ui-topbar-right {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}
.ui-topbar-brand {
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  border-radius: var(--ui-radius-md);
  transition: background-color var(--ui-dur-fast) var(--ui-ease);
}
.ui-topbar-brand:hover {
  background: var(--ui-surface-sunken);
}
.ui-topbar-brand img {
  height: 48px;
  width: auto;
  max-width: 180px;
  object-fit: contain;
}

/* Notification bell with pulsing dot */
.ui-topbar-bell {
  position: relative;
}
.ui-topbar-bell:hover,
.ui-topbar-bell[aria-expanded="true"] {
  color: #FFFFFF;
  background: var(--ui-accent);
  border-color: var(--ui-accent-strong);
}
.ui-topbar-count {
  position: absolute;
  top: -3px;
  right: -3px;
  display: grid;
  place-items: center;
  min-width: 18px;
  height: 18px;
  padding: 0 4px;
  border-radius: var(--ui-radius-pill);
  background: var(--ui-accent);
  color: #FFFFFF;
  font-size: 10px;
  font-weight: var(--ui-fw-black);
  box-shadow: 0 0 0 2px var(--ui-surface);
}

/* Alerts list */
.ui-alerts-menu {
  width: 340px;
  max-width: calc(100vw - 32px);
}
.ui-alerts-readall {
  margin-left: auto;
  padding: 0;
  border: 0;
  background: none;
  color: var(--ui-accent-text);
  font-size: var(--ui-fs-micro);
  font-weight: var(--ui-fw-bold);
}
.ui-alerts-list {
  max-height: 360px;
  overflow-y: auto;
}
.ui-alert-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 100%;
  padding: 8px 12px;
  border: 0;
  border-radius: var(--ui-radius-sm);
  background: none;
  color: var(--ui-text);
  text-align: left;
  white-space: normal;
}
.ui-alert-item:hover {
  background: var(--ui-surface-sunken);
}
.ui-alert-item--unread {
  background: var(--ui-accent-soft);
}
.ui-alert-title {
  font-size: var(--ui-fs-sm);
  font-weight: var(--ui-fw-bold);
}
.ui-alert-body {
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-micro);
}
.ui-alert-date {
  color: var(--ui-text-faint);
  font-size: var(--ui-fs-micro);
}

/* Profile pill */
.ui-topbar-profile {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 4px 14px 4px 4px;
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius-pill);
  background: var(--ui-surface-sunken);
  color: var(--ui-text);
  text-align: left;
  transition: background-color var(--ui-dur-fast) var(--ui-ease), box-shadow var(--ui-dur-fast) var(--ui-ease);
}
.ui-topbar-profile:hover,
.ui-topbar-profile[aria-expanded="true"] {
  background: var(--ui-surface);
  box-shadow: 0 6px 16px rgba(var(--ui-accent-rgb), 0.12);
}
.ui-topbar-avatar {
  position: relative;
  flex: 0 0 auto;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: var(--ui-accent);
  box-shadow: 0 4px 12px rgba(var(--ui-accent-rgb), 0.3);
}
.ui-topbar-avatar img {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
}
.ui-topbar-online {
  position: absolute;
  right: -1px;
  bottom: -1px;
  width: 11px;
  height: 11px;
  border-radius: 50%;
  border: 2px solid var(--ui-surface);
  background: var(--ui-success);
}
.ui-topbar-profile-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
  line-height: 1.2;
}
.ui-topbar-name {
  max-width: 160px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: var(--ui-fs-sm);
  font-weight: var(--ui-fw-black);
}
.ui-topbar-role {
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-micro);
  font-weight: var(--ui-fw-semibold);
}
.ui-topbar-chevron {
  color: var(--ui-text-muted);
  font-size: 13px;
  transition: transform var(--ui-dur) var(--ui-ease);
}
.ui-topbar-profile[aria-expanded="true"] .ui-topbar-chevron {
  transform: rotate(180deg);
}

/* Dropdown panels */
.ui-topbar-right .dropdown {
  position: relative;
}
/* Static positioning (data-bs-display="static"): anchor under the toggle, right-aligned */
.ui-topbar-menu {
  position: absolute;
  top: 100% !important;
  right: 0 !important;
  left: auto !important;
  transform: none !important;
  min-width: 240px;
  margin-top: 10px !important;
}
.ui-topbar-menu .dropdown-item {
  display: flex;
  align-items: center;
  gap: 10px;
}
.ui-topbar-menu .dropdown-item i {
  color: var(--ui-accent-text);
  font-size: 17px;
}
.ui-topbar-menu .dropdown-item.text-danger i {
  color: var(--ui-danger);
}
.ui-topbar-menu-head {
  display: flex;
  align-items: baseline;
  gap: 6px;
  padding: 8px 12px 10px;
  margin-bottom: 6px;
  border-bottom: 1px solid var(--ui-border);
  color: var(--ui-text);
  font-size: var(--ui-fs-sm);
  font-weight: var(--ui-fw-black);
}
.ui-topbar-menu-user {
  flex-direction: column;
  gap: 2px;
}
.ui-topbar-menu-muted {
  color: var(--ui-text-muted);
  font-weight: var(--ui-fw-semibold);
}
.ui-topbar-menu-online {
  color: var(--ui-success);
  font-size: var(--ui-fs-micro);
  font-weight: var(--ui-fw-bold);
}
.ui-topbar-menu-empty {
  margin: 0;
  padding: 14px 12px 10px;
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-sm);
  text-align: center;
}

/* Responsive */
@media (min-width: 1200px) {
  .ui-topbar-burger {
    display: none;
  }
}
@media (max-width: 767.98px) {
  .ui-topbar {
    height: 60px;
    padding: 0 8px 0 8px;
  }
  .ui-topbar-brand img {
    height: 38px;
    max-width: 120px;
  }
  .ui-topbar-profile {
    padding: 3px;
  }
  .ui-topbar-profile-text,
  .ui-topbar-chevron {
    display: none;
  }
  .ui-topbar-right {
    gap: 6px;
  }
  /* Phones: open menus as a full-width panel just below the header */
  .ui-topbar-menu {
    position: fixed !important;
    top: calc(var(--ui-frame-gap) + 60px) !important;
    left: var(--ui-frame-gap) !important;
    right: var(--ui-frame-gap) !important;
    width: auto !important;
    min-width: 0;
    max-width: none !important;
    max-height: calc(100vh - var(--ui-frame-gap) * 2 - 80px);
    overflow-y: auto;
  }
}
</style>
